import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  Fab,
  Badge,
  Drawer,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Pagination,
} from '@mui/material';
import {
  FilterList,
  Close,
  Compare,
  ViewList,
  ViewModule,
  Refresh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// 组件导入
import ProgramSearch from '../components/programs/ProgramSearch';
import ProgramFilters from '../components/programs/ProgramFilters';
import ProgramCard from '../components/programs/ProgramCard';
import ProgramComparison from '../components/programs/ProgramComparison';
import MultiLoginDialog from '../components/common/MultiLoginDialog';
import { useUser } from '../contexts/UserContext';

// API服务导入
import { programService, programDataMapper } from '../services/programService';

// 静态数据导入（作为后备）
import { programSortOptions, programLevels, programDurations, intakeOptions } from '../data/programsData';

const Programs = () => {
  const navigate = useNavigate();

  // 状态管理
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    level: '',
    duration: '',
    intake: '',
    tuitionRange: [0, 1000000],
  });
  const [sortBy, setSortBy] = useState('tuition-low');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [programs, setPrograms] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [favoritePrograms, setFavoritePrograms] = useState(new Set());
  const [comparePrograms, setComparePrograms] = useState(new Set());
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [loginOpen, setLoginOpen] = useState(false);
  const { isLoggedIn: isAuthenticated } = useUser();

  // API相关状态
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(12);

  // 筛选选项状态
  const [filterOptions, setFilterOptions] = useState({
    levels: programLevels,
    durations: programDurations,
    intakes: intakeOptions,
  });

  // 首次加载标记
  const hasInitialLoadRef = useRef(false);

  // 从localStorage加载收藏
  useEffect(() => {
    const savedFavorites = localStorage.getItem('programFavorites');
    if (savedFavorites) {
      try {
        setFavoritePrograms(new Set(JSON.parse(savedFavorites)));
      } catch (err) {
        console.error('加载专业收藏失败:', err);
      }
    }
  }, []);

  // 保存收藏到localStorage
  useEffect(() => {
    localStorage.setItem('programFavorites', JSON.stringify([...favoritePrograms]));
  }, [favoritePrograms]);

  // 加载程序数据
  const loadPrograms = useCallback(async (page = 1, forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      const sortParams = programDataMapper.mapSortToApiParams(sortBy);
      const filterParams = programDataMapper.mapFiltersToApiParams(filters, searchTerm);

      const apiParams = { page: page - 1, size: pageSize, ...sortParams, ...filterParams };

      const response = await programService.searchPrograms(apiParams);
      const data = response;

      let programsArray = [];
      let totalElements = 0;
      let totalPages = 1;

      if (data?.data?.data && Array.isArray(data.data.data)) {
        programsArray = data.data.data;
        totalElements = data.data.total || 0;
        totalPages = data.data.totalPages || Math.ceil(totalElements / pageSize);
      } else if (Array.isArray(data?.data?.content)) {
        programsArray = data.data.content;
        totalElements = data.data.totalElements || data.data.total || programsArray.length;
        totalPages = data.data.totalPages || Math.ceil(totalElements / pageSize);
      } else if (Array.isArray(data?.data)) {
        programsArray = data.data;
        totalElements = data.total || programsArray.length;
        totalPages = data.totalPages || Math.ceil(totalElements / pageSize);
      } else if (Array.isArray(data)) {
        programsArray = data;
        totalElements = programsArray.length;
        totalPages = Math.ceil(totalElements / pageSize);
      } else {
        setPrograms([]);
        setFilteredPrograms([]);
        setTotalElements(0);
        setTotalPages(1);
        return;
      }

      const mappedPrograms = programsArray.map(program => programDataMapper.mapProgramFromApi(program));

      setPrograms(mappedPrograms);
      setFilteredPrograms(mappedPrograms);
      setTotalElements(totalElements);
      setTotalPages(totalPages);
      setCurrentPage(page);

      if (forceRefresh) showSnackbar('数据已刷新', 'success');
    } catch (err) {
      setError(err.message || '加载专业数据失败');
      setPrograms([]);
      setFilteredPrograms([]);
      setTotalElements(0);
      setTotalPages(1);
      showSnackbar(`加载失败: ${err.message || '请检查网络连接'}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, sortBy, pageSize]);

  // 加载筛选选项
  const loadFilterOptions = useCallback(async () => {
    try {
      const [levelsRes, intakesRes] = await Promise.all([
        programService.getDegreeTypes().catch(() => null),
        programService.getIntakeOptions().catch(() => null)
      ]);

      const newFilterOptions = { ...filterOptions };

      if (levelsRes?.data && Array.isArray(levelsRes.data)) {
        newFilterOptions.levels = levelsRes.data.map(level => ({
          value: level.name || level.value || level,
          label: level.nameCn || level.label || level.name || level
        }));
      }

      if (intakesRes?.data && Array.isArray(intakesRes.data)) {
        newFilterOptions.intakes = intakesRes.data.map(intake => ({
          value: intake.name || intake.value || intake,
          label: intake.nameCn || intake.label || intake.name || intake
        }));
      }

      setFilterOptions(newFilterOptions);
    } catch (err) {
      // 加载筛选选项失败，使用默认选项
    }
  }, [filterOptions]);

  // 搜索/筛选防抖 + 初次加载
  useEffect(() => {
    if (!hasInitialLoadRef.current) {
      loadPrograms(1);
      hasInitialLoadRef.current = true;
      return;
    }

    const timer = setTimeout(() => {
      loadPrograms(1);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, filters, sortBy]);

  // 页码变化加载
  useEffect(() => {
    if (currentPage > 1) {
      loadPrograms(currentPage);
    }
  }, [currentPage]);

  // 处理函数
  const handleSearchChange = (term) => setSearchTerm(term);

  const handleFilterChange = (newFilters) => setFilters(newFilters);

  const handleClearFilters = () => {
    setFilters({ level: '', duration: '', intake: '', tuitionRange: [0, 1000000] });
    setSearchTerm('');
  };

  const handleRefresh = () => loadPrograms(currentPage, true);

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFavoriteToggle = (programId) => {
    const newFavorites = new Set(favoritePrograms);
    if (newFavorites.has(programId)) newFavorites.delete(programId);
    else newFavorites.add(programId);
    setFavoritePrograms(newFavorites);
  };

  const handleCompareToggle = (programId) => {
    const newCompare = new Set(comparePrograms);
    if (newCompare.has(programId)) newCompare.delete(programId);
    else if (newCompare.size < 3) newCompare.add(programId);
    else {
      showSnackbar('最多只能对比3个专业', 'warning');
      return;
    }
    setComparePrograms(newCompare);
  };

  const handleViewDetails = (programId) => navigate(`/programs/${programId}`);

  const handleApplyNow = (program) => {
    if (!isAuthenticated) { setLoginOpen(true); return; }
    if (!program) { showSnackbar('请先选择一个专业', 'warning'); return; }
    navigate('/services', { state: { selectedProgram: { id: program.id, degree: program.academicDegree, name: program.name } } });
  };

  const showSnackbar = (message, severity = 'info') => setSnackbar({ open: true, message, severity });
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });
  const handleLoginSuccess = () => { setLoginOpen(false); };

  // 渲染函数
  const renderPrograms = () => {
    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={60} /></Box>;
    if (error) return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={handleRefresh} startIcon={<Refresh />}>重新加载</Button>
      </Box>
    );
    if (filteredPrograms.length === 0) return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>未找到符合条件的专业</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>尝试调整搜索条件或筛选选项</Typography>
        <Button variant="outlined" onClick={handleClearFilters}>重置筛选条件</Button>
      </Box>
    );

    return (
      <>
        <Grid container spacing={3}>
          {filteredPrograms.map(program => (
            <Grid item xs={12} sm={viewMode === 'grid' ? 6 : 12} md={viewMode === 'grid' ? 4 : 12} lg={viewMode === 'grid' ? 3 : 12} key={program.id} sx={{ display: 'flex' }}>
              <ProgramCard
                program={program}
                isFavorite={favoritePrograms.has(program.id)}
                isCompared={comparePrograms.has(program.id)}
                onFavoriteToggle={() => handleFavoriteToggle(program.id)}
                onCompareToggle={() => handleCompareToggle(program.id)}
                onViewDetails={() => handleViewDetails(program.id)}
                onApplyNow={() => handleApplyNow(program)}
                viewMode={viewMode}
              />
            </Grid>
          ))}
        </Grid>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" size="large" showFirstButton showLastButton />
          </Box>
        )}
      </>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>专业搜索</Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>找到最适合的专业课程</Typography>
      </Box>

      <ProgramSearch searchTerm={searchTerm} onSearchChange={handleSearchChange} resultCount={totalElements} loading={loading} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
          <ProgramFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filteredCount={filteredPrograms.length}
            totalCount={totalElements}
            filterOptions={filterOptions}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>排序方式</InputLabel>
              <Select value={sortBy} label="排序方式" onChange={e => setSortBy(e.target.value)}>
                {programSortOptions.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <Button variant="outlined" startIcon={<FilterList />} onClick={() => setMobileFiltersOpen(true)} size="small">筛选</Button>
              </Box>
              <IconButton onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')} size="small">
                {viewMode === 'grid' ? <ViewList /> : <ViewModule />}
              </IconButton>
            </Stack>
          </Box>

          {renderPrograms()}
        </Grid>
      </Grid>

      <Drawer anchor="left" open={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} sx={{ display: { xs: 'block', md: 'none' } }}>
        <Box sx={{ width: 300, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">筛选条件</Typography>
            <IconButton onClick={() => setMobileFiltersOpen(false)}><Close /></IconButton>
          </Box>
          <ProgramFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filteredCount={filteredPrograms.length}
            totalCount={totalElements}
            filterOptions={filterOptions}
          />
        </Box>
      </Drawer>

      {comparePrograms.size > 0 && (
        <Fab color="primary" sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }} onClick={() => setComparisonOpen(true)}>
          <Badge badgeContent={comparePrograms.size} color="error"><Compare /></Badge>
        </Fab>
      )}

      <Drawer anchor="bottom" open={comparisonOpen} onClose={() => setComparisonOpen(false)} sx={{ '& .MuiDrawer-paper': { height: '80vh', borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}>
        <ProgramComparison
          programIds={Array.from(comparePrograms)}
          programs={programs}
          onClose={() => setComparisonOpen(false)}
          onRemoveProgram={handleCompareToggle}
          onViewDetails={handleViewDetails}
          onApplyNow={handleApplyNow}
        />
      </Drawer>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      <MultiLoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} onLoginSuccess={handleLoginSuccess} />
    </Container>
  );
};

export default Programs;
